import React, { useMemo } from 'react';
import { useEffect } from 'react';
import connect from 'react-redux/es/connect/connect';
import { flushQueuedEvents, EcommerceTracker } from '../tools/EcommerceTracker';

const GAContext = React.createContext();

function GoogleAnalyticsProvider({ user, client, children }) {
    const gaTrackingId = client?.gaTrackingId;
    const isRegularUser = !user || user.role === 'customer';

    useEffect(() => {
        if (!isRegularUser || !gaTrackingId) {
            return;
        }

        // Initialize dataLayer and gtag if they don't exist
        window.dataLayer = window.dataLayer || [];
        if (!window.gtag) {
            window.gtag = function() {
                window.dataLayer.push(arguments);
            };
        }

        const existingScript = document.querySelector(
            `script[src*="googletagmanager.com/gtag/js?id=${gaTrackingId}"]`
        );

        // Check if this tracking ID is already configured
        const isAlreadyConfigured = window.dataLayer?.some(
            item => Array.isArray(item) && item[0] === 'config' && item[1] === gaTrackingId
        );

        const initGtag = () => {
            if (!isAlreadyConfigured) {
                window.gtag('js', new Date());
                window.gtag('config', gaTrackingId, {
                    user_id: user?.id || null,
                    debug_mode: true
                });
            }
            flushQueuedEvents();
        };

        if (!existingScript) {
            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${gaTrackingId}`;
            script.onload = initGtag;
            document.head.appendChild(script);
        } else if (!isAlreadyConfigured) {
            initGtag();
        }
    }, [gaTrackingId, isRegularUser, user?.id]);

    const memoizedValues = useMemo(
        () => ({
            track: EcommerceTracker
        }),
        []
    );

    return <GAContext.Provider value={memoizedValues}>{children}</GAContext.Provider>;
}

GAContext.displayName = 'GoogleAnalyticsContext';

/**
 *
 * @returns {{ track: typeof EcommerceTracker, helpers: typeof helpers }}
 */
export function useGA() {
    return React.useContext(GAContext);
}

export default connect(state => ({ user: state.user.user }))(GoogleAnalyticsProvider);
